import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../../context-state/AuthContext.jsx";
import { SHOPIFY_SCOPE, SHOPIFY_CLIENT_ID } from "../../config-global.jsx";
import { toast } from "sonner";
import * as API from "@/services/api.js";
import { handleError } from "@/services/errorHandler.js";
import { BASE_PATH_AFTER_SUCCESSFUL_AUTH } from '@/consts/index.js'
import { FormBlock } from '@/components/forms/FormBlock.jsx'
import { Input } from '@/components/ui/input.jsx'
import { Button } from '@/components/ui/button.jsx'
import * as z from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { motion } from 'framer-motion'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card.jsx'
import { CircleLoader } from '@/components/loader/CircleLoader.jsx'
import Logo from '@/assets/logos/spring.png'
import { LegalApproval } from '@/pages/auth/LegalApproval.jsx'
import { TypographySmallMuted } from '@/components/ui/typography.jsx'

const SignInPage = () => {

    const { authenticate, user } = useAuthContext()
    const navigate = useNavigate()
    const [isLoading, setIsLoading] = useState(true)

    const formSchema = z.object({
        email: z.string().min(1, 'Email is required').email('Invalid email'),
        password: z.string().min(1, 'Password is required')
    })

    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            name: "",
            password: "",
        },
    })


    // This is the INSTALL entry-point: 
    //
    // https://colab-client-staging.vercel.app/
    // ?hmac=7b29e5fc854b5278a5cc2e9f8f237495a46b1f0841a6cccbbad8c08fcdb10fb6
    // &host=YWRtaW4uc2hvcGlmeS5jb20vc3RvcmUvcm9uLXRlc3QtZGV2LWFnYWlu
    // &shop=ron-test-dev-again.myshopify.com
    // &timestamp=1746427550
    //
    // The new flow
    // 1. If the user get here for the first time from Shopify and have the shopify query params, redirect to Shopify "/admin/oauth/authorize"
    // 2. If the user get here the 'shop' already exists (integration ? account) need regular login, to the account's of the store
    //   2a. So if the shop have an account assign to it, need to login (not allow register here) and log into the account
    //   2b. 



    const initAuth = async () => {
        try {
            const queryParams = new URLSearchParams(window.location.search)
            const shopDomain = queryParams.get('shop')
            // const isCustomFlow = queryParams.get('customFlow') || queryParams.get('customflow')

            if (user && !shopDomain) {
                navigate(BASE_PATH_AFTER_SUCCESSFUL_AUTH)
                return
            }

            if (shopDomain) {
                const redirect_uri = `${window.location.origin}/connect/shopify`
                const shopifyAuthUrl = `https://${shopDomain}/admin/oauth/authorize?client_id=${SHOPIFY_CLIENT_ID}&scope=${SHOPIFY_SCOPE}&redirect_uri=${redirect_uri}&state=abcc` // &grant_options[]={access_mode}
                window.location.replace(shopifyAuthUrl)
                return
            }

            setIsLoading(false)

            // TOOD - if we have shopDomain but the user already exists.

            // This is only for hte custom app flow!
            // if (shopDomain && isCustomFlow) {
            //     const { data } = await API.authStore({ storeDomain: shopDomain, authCode: "", scope: SHOPIFY_SCOPE, host: window.location.host })
            //     await authenticate(data.user)
            //     navigate(BASE_PATH_AFTER_SUCCESSFUL_AUTH)
            //     return
            // }

            // TODO - check if we need this.
            // if (!shopDomain) {
            // console.log('shopDomain')
            // await authenticate()
            // return
            // }

            // Continue shopify Oauth flow. this will be required only once when the user install the app for the first time.
            // const redirect_uri = `${window.location.origin}/connect/shopify`
            // const shopifyAuthUrl = `https://${shopDomain}/admin/oauth/authorize?client_id=${SHOPIFY_CLIENT_ID}&scope=${SHOPIFY_SCOPE}&redirect_uri=${redirect_uri}&state=abcc` // &grant_options[]={access_mode}

            // window.location.replace(shopifyAuthUrl)
        } catch (e) {
            console.log(e)
            toast.error(e.message)
            handleError(e)
            setIsLoading(false)
        }

    }

    const manuallyLogIn = async (formData) => {
        try {
            setIsLoading(true)
            const { data } = await API.logInUser({ email: formData.email.toLowerCase(), password: formData.password })

            await authenticate(data)
            toast.success('Welcome back to Upspring!')
            navigate(BASE_PATH_AFTER_SUCCESSFUL_AUTH)

        } catch (e) {
            console.error(e)
            toast.error(e.message)
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        initAuth()
    }, [user])

    return (
        <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", bounce: 0.4 }}
            className="w-full max-w-md"
        >
            <Card className="w-full max-w-md shadow-lg  backdrop-blur-md relative bg-background/70">
                {
                    isLoading && (
                        <div className="w-full h-full absolute z-10 flex justify-center items-center bg-background/70">
                            <CircleLoader className="top-0 left-0" />
                        </div>
                    )
                }
                <CardHeader>
                    <CardTitle className="text-center">
                        <img src={Logo} className="m-auto w-36" alt="spring-ai-logo" />
                    </CardTitle>
                    <CardDescription className="text-center">
                        Welcome Back!
                        <br />
                        Please sign in to your account
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit(manuallyLogIn)} className="flex flex-col gap-4">
                        <FormBlock required label="Email">
                            <Input
                                {...register("email")}
                                type="email"
                                error={errors.email?.message}
                            />
                        </FormBlock>
                        <FormBlock required label="Password">
                            <Input
                                {...register("password")}
                                type="password"
                                error={errors.password?.message}
                            />
                        </FormBlock>
                        <Button type="submit" className="w-full">Log in</Button>
                    </form>

                </CardContent>
                <CardFooter className="flex justify-center flex-col gap-4">
                    <TypographySmallMuted className="hover:underline text-center cursor-pointer" onClick={() => navigate('/auth/register')}>
                        Don't have an account? Register
                    </TypographySmallMuted>
                    <LegalApproval />
                </CardFooter>
            </Card>
        </motion.div>
    )
}

export default SignInPage
